import React, { useState } from 'react';
import API from '../api/index.js';
import { clearApiDataCache, clearTenantSession } from '../api/api1.js';
import { clearServiceApiCache } from '../services/api.js';
import { clearSeatsCache } from '../utils/seatsCache.js';
import { clearSeatTypesCache } from '../services/seatTypes.js';
import { DEMO_LOGIN_ENABLED } from '../demoMode.js';
import { touchSession } from '../utils/sessionTimeout.js';

// Same rule as the tenant login: the account email must end in .com.
const LOGIN_EMAIL_REGEX = /^[^\s@]+@(?:[a-zA-Z0-9-]+\.)+com$/i;
const LOGIN_STROKE = {
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.8,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
};
const LOGIN_GLYPHS = {
  book: (
    <>
      <path d="M12 6.2C10.6 5.2 8.7 4.7 6.5 4.7H4v13.6h2.6c2.2 0 4.1.5 5.4 1.5 1.3-1 3.2-1.5 5.4-1.5H20V4.7h-2.5c-2.2 0-4.1.5-5.5 1.5Z" />
      <path d="M12 6.2v13.6" />
    </>
  ),
  cap: (
    <>
      <path d="m3 9 9-4.5L21 9l-9 4.5L3 9Z" />
      <path d="M6.8 10.8V16c0 1.4 2.3 2.5 5.2 2.5s5.2-1.1 5.2-2.5v-5.2" />
      <path d="M21 9v5" />
    </>
  ),
  users: (
    <>
      <circle cx="9" cy="8" r="3" />
      <path d="M3.5 19.5a5.5 5.5 0 0 1 11 0" />
      <path d="M16 5.3a3 3 0 0 1 0 5.8M17.5 14.4a5.5 5.5 0 0 1 3 4.3" />
    </>
  ),
  chart: (
    <>
      <path d="M4 20V4M4 20h16" />
      <path d="M8 17v-5M13 17V8M18 17v-9" />
    </>
  ),
  eye: (
    <>
      <path d="M2.5 12S6 5.5 12 5.5 21.5 12 21.5 12 18 18.5 12 18.5 2.5 12 2.5 12Z" />
      <circle cx="12" cy="12" r="3" />
    </>
  ),
  eyeOff: (
    <>
      <path d="m3 3 18 18" />
      <path d="M10.6 10.7a2 2 0 0 0 2.7 2.7" />
      <path d="M9.9 5.6A10.9 10.9 0 0 1 12 5.5c6 0 9.5 6.5 9.5 6.5a18.6 18.6 0 0 1-3 4M6.2 6.2C4 7.8 2.5 12 2.5 12S6 18.5 12 18.5c1.5 0 2.9-.4 4.1-1" />
    </>
  ),
};

export function LoginPage({ onLogin }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [show, setShow] = useState(false);
  const [error, setError] = useState('');
  const [errorHint, setErrorHint] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [forgotPassword, setForgotPassword] = useState(false);
  const [resetNotice, setResetNotice] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const clearMessages = () => { setError(''); setErrorHint(''); setSuccess(''); };
  const submit = async (e) => {
    e.preventDefault();
    if (loading || success) return;
    clearMessages();
    if (!email.trim() || !password) {
      setError('Email and password are required');
      return;
    }
    if (!DEMO_LOGIN_ENABLED && !LOGIN_EMAIL_REGEX.test(email.trim())) {
      setError('Please enter a valid email address ending with .com (e.g., test@gmail.com)');
      return;
    }
    if (DEMO_LOGIN_ENABLED) {
      touchSession();
      onLogin(); return;
    }
    setLoading(true);
    clearTenantSession();
    clearApiDataCache();
    clearServiceApiCache();
    clearSeatsCache();
    clearSeatTypesCache();
    try {
      // Keep the email exactly as typed so the login stays case-sensitive,
      // matching the tenant login flow.
      const res = await API.post('/api/login', { email: email.trim(), password });
      const data = res.data || {};
      const loginUser = data.user || {};
      const loginTenant = data.tenant || {};
      const userInfo = { email: loginUser.email, role: loginUser.role, ...loginUser };
      const tenantInfo = {
        id: loginTenant.id || loginTenant.tenantId || loginTenant.tenant_id,
        tenantId: loginTenant.id || loginTenant.tenantId || loginTenant.tenant_id,
        ...loginTenant,
      };

      if (data.user) {
        localStorage.setItem('userinfo', JSON.stringify(userInfo));
        if (loginUser.role) {
          localStorage.setItem('userRole', loginUser.role);
          localStorage.setItem('user_type', loginUser.role);
        }
      }
      if (data.tenant) {
        localStorage.setItem('tenantinfo', JSON.stringify(tenantInfo));
        const tenantId = loginTenant.tenant_id || loginTenant.tenantId || loginTenant.id;
        if (tenantId !== undefined && tenantId !== null) {
          localStorage.setItem('tenantsubdomain', String(tenantId));
          localStorage.setItem('tenant-subdomain', String(tenantId));
          localStorage.setItem('tenantId', String(tenantId));
        }
      }
      // Token is now stored in HttpOnly cookie by the backend
      touchSession();

      // Best-effort refresh for absolute profile/logo URLs. A failure here
      // must never block an otherwise valid login.
      try {
        const profileRes = await API.get('/api/users/me');
        const profile = profileRes.data || {};
        const profileUser = profile.user || profile.data?.user;
        const profileTenant = profile.tenant || profile.data?.tenant;
        if (profileUser) localStorage.setItem('userinfo', JSON.stringify({ ...userInfo, ...profileUser }));
        if (profileTenant) localStorage.setItem('tenantinfo', JSON.stringify({ ...tenantInfo, ...profileTenant }));
      } catch (profileError) {
        console.warn('Could not refresh profile details after login:', profileError);
      }

      setSuccess('Login successful! Redirecting...');
      window.setTimeout(() => onLogin(data), 700);
    } catch (err) {
      const message = err?.response?.data?.error || err?.response?.data?.message || err?.message || 'Invalid email or password';
      setError(message);
      if (/inactive|deactivated|suspended|blocked/i.test(message)) {
        setErrorHint('Please contact your tenant owner to reactivate your account.');
      }
    } finally { setLoading(false); }
  };
  const requestPasswordReset = async (e) => {
    e.preventDefault();
    setError(''); setErrorHint(''); setResetNotice('');
    if (!email.trim()) { setError('Enter the email address for your account.'); return; }
    if (DEMO_LOGIN_ENABLED) {
      setResetNotice('Password reset is not connected in preview mode.');
      return;
    }
    setResetLoading(true);
    try {
      await API.post('/api/forgot-password', { email: email.trim().toLowerCase() });
      setResetNotice('If an account uses this email, password reset instructions have been sent.');
    } catch {
      setError('Could not send reset instructions. Please try again or contact your administrator.');
    } finally { setResetLoading(false); }
  };
  return (
    <main className="login-screen">
      <section className="login-brand-panel">
        <div className="login-brand-top">
          <span className="login-brand-icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" {...LOGIN_STROKE}>{LOGIN_GLYPHS.book}</svg>
          </span>
          <span className="login-brand-name">Study Room</span>
        </div>
        <div className="login-copy">
          <h1>Manage your<br/><span>study spaces</span><br/>effortlessly.</h1>
          <p>The all-in-one platform for library and study room owners to manage students, seats, and payments.</p>
        </div>
        <div className="login-features">
          <div className="login-feat">
            <span className="login-feat-icon" aria-hidden="true">
              <svg viewBox="0 0 24 24" {...LOGIN_STROKE}>{LOGIN_GLYPHS.cap}</svg>
            </span>
            <span>Student enrollment &amp; tracking</span>
          </div>
          <div className="login-feat">
            <span className="login-feat-icon" aria-hidden="true">
              <svg viewBox="0 0 24 24" {...LOGIN_STROKE}>{LOGIN_GLYPHS.users}</svg>
            </span>
            <span>Seat &amp; room management</span>
          </div>
          <div className="login-feat">
            <span className="login-feat-icon" aria-hidden="true">
              <svg viewBox="0 0 24 24" {...LOGIN_STROKE}>{LOGIN_GLYPHS.chart}</svg>
            </span>
            <span>Payment collection &amp; analytics</span>
          </div>
        </div>
        <div className="login-trusted">Trusted by 500+ libraries across Maharashtra</div>
        <div className="login-circles"><div className="lc c1"></div><div className="lc c2"></div><div className="lc c3"></div></div>
      </section>
      <section className="login-form-panel">
        <div className="login-form-wrap">
          <h2>{forgotPassword ? 'Reset your password' : 'Welcome back'}</h2>
          <p className="login-form-sub">{forgotPassword ? "Enter your account email and we'll send reset instructions." : 'Sign in to your Study Room account'}</p>
          {forgotPassword ? (
            <form className="login-card" onSubmit={requestPasswordReset}>
              <label className="login-field">Email <em>*</em><input type="email" required autoComplete="email" value={email} onChange={e=>setEmail(e.target.value)} placeholder="admin@studyroom.in" /></label>
              {error && (
                <div className="login-error" role="alert">
                  {error}
                  {errorHint && <span className="login-error-hint">{errorHint}</span>}
                </div>
              )}
              {resetNotice && <div className="login-success" role="status">{resetNotice}</div>}
              <button className="login-submit" type="submit" disabled={resetLoading}>{resetLoading?'Sending...':'Send reset instructions'}</button>
              <button type="button" className="login-back" onClick={()=>{setForgotPassword(false);clearMessages();setResetNotice('');}}>&larr; Back to sign in</button>
            </form>
          ) : (
            <form className="login-card" onSubmit={submit}>
              <label className="login-field">Email <em>*</em><input type={DEMO_LOGIN_ENABLED? 'text' : 'email'} required autoComplete="email" autoCapitalize="none" spellCheck={false} value={email} onChange={e=>setEmail(e.target.value)} placeholder="admin@studyroom.in" /></label>
              <label className="login-field">
                Password <em>*</em>
                <span className="password-input">
                  <input
                    type={show ? 'text' : 'password'}
                    required
                    autoComplete="current-password"
                    className="password-input-no-reveal"
                    value={password}
                    onChange={e=>setPassword(e.target.value)}
                    placeholder="Enter your password"
                  />
                  <button
                    type="button"
                    className="password-toggle"
                    onClick={()=>setShow(v=>!v)}
                    aria-label={show ? 'Hide password' : 'Show password'}
                    aria-pressed={show}
                    title={show ? 'Hide password' : 'Show password'}
                  >
                    <svg viewBox="0 0 24 24" {...LOGIN_STROKE}>{show ? LOGIN_GLYPHS.eye : LOGIN_GLYPHS.eyeOff}</svg>
                  </button>
                </span>
              </label>
              <div className="login-options"><button type="button" onClick={()=>{setForgotPassword(true);clearMessages();setResetNotice('');}}>Forgot password?</button></div>
              {error && (
                <div className="login-error" role="alert">
                  {error}
                  {errorHint && <span className="login-error-hint">{errorHint}</span>}
                </div>
              )}
              {success && <div className="login-success" role="status">{success}</div>}
              <button className="login-submit" type="submit" disabled={loading || Boolean(success)}>
                {loading ? 'Signing in...' : success ? 'Signed in' : 'Sign In'}
              </button>
            </form>
          )}
          <div className="login-footer">&copy; 2026 Study Room &middot; Built for focused spaces</div>
        </div>
      </section>
    </main>
  );
}
